/**
 * Centralized glossary of every term / concept used in the LaMoney platform.
 * The key is a stable slug — components reference this slug via <InfoTip term="..." />.
 */
export const glossary: Record<string, { title: string; body: string }> = {
  /* ─── P&L & Valuation ─── */
  pnl: {
    title: 'P&L (Profit & Loss)',
    body: 'REALIZED profit or loss from positions that have been CLOSED today. Calculation: (Sell Price − Buy Price) × Quantity. This is final — money has been debited/credited. It only includes trades where both entry and exit happened. Overnight carry-forward positions are NOT included until they are actually closed.',
  },
  mtm: {
    title: 'MTM (Mark-to-Market)',
    body: 'UNREALIZED profit or loss on positions that are STILL OPEN. Calculation: (Current Market Price − Entry Price) × Quantity. This changes every second with live prices. If you close the position now, this MTM becomes your realized P&L. Key difference from P&L: P&L = already closed. MTM = still open, fluctuating.',
  },
  totalMtm: {
    title: 'Total MTM',
    body: 'Sum of MTM across ALL execution sets. Formula: Σ (Current LTP − Entry Price) × Qty for every open position. This is your live portfolio-level unrealized P&L. It does NOT include already-closed (realized) trades — for that, see Total P&L.',
  },
  realizedPnl: {
    title: 'Realized P&L',
    body: 'Profit/loss from trades where BOTH entry and exit are complete. This is locked in and final — market movements no longer affect it. Example: Sold NIFTY 24500 CE at ₹200, bought back at ₹150 → Realized P&L = +₹50 × lot size. Overnight positions become realized only when you close them the next day.',
  },
  unrealizedPnl: {
    title: 'Unrealized P&L',
    body: 'Same as MTM. Profit/loss on positions NOT yet closed. It fluctuates with every price tick. When you close the position, unrealized becomes realized. Important: Unrealized gains are not guaranteed — the market could reverse before you exit.',
  },
  premium: {
    title: 'Premium Collected',
    body: 'The price received when SELLING an option. For option sellers (writers), premium = maximum possible profit. If the option expires worthless, you keep the entire premium. Calculation: Option Sell Price × Lot Size × Number of Lots. This is the entry price, not the P&L.',
  },
  strategyMtm: {
    title: 'Strategy MTM',
    body: 'MTM grouped by strategy type. RSI: combined MTM of all RSI Straddle sets. Trend: combined MTM of all Trending sets. Helps you see which strategy is performing better RIGHT NOW (not historically).',
  },
  cumulativePnl: {
    title: 'Cumulative P&L',
    body: 'Running total of REALIZED daily P&L over a period. Day 1 P&L + Day 2 P&L + ... = Cumulative. The chart shows your equity curve. This only counts closed/realized trades, not open MTM. An upward slope = consistent profitability. Flat = break-even. Downward = losing streak.',
  },

  /* ─── Greeks ─── */
  delta: {
    title: 'Delta (Δ)',
    body: 'Measures how much the option price changes for every ₹1 move in the underlying. A delta of 0.5 means the option price moves ₹0.50 for every ₹1 move in the stock.',
  },
  gamma: {
    title: 'Gamma (Γ)',
    body: 'Measures the rate of change of Delta. High gamma means Delta can change rapidly, making the position more sensitive to underlying price movements.',
  },
  theta: {
    title: 'Theta (Θ)',
    body: 'Time decay — the amount the option loses in value each day. For option sellers, positive theta is beneficial as sold options lose value over time.',
  },
  vega: {
    title: 'Vega (ν)',
    body: 'Measures sensitivity to volatility. A vega of 5 means the option price changes by ₹5 for every 1% change in implied volatility.',
  },

  /* ─── VIX & Volatility ─── */
  vixRsi: {
    title: 'VIX RSI Indicator',
    body: 'Combines India VIX (market fear index) with RSI (momentum oscillator). When VIX RSI crosses above 65, the system enters cooldown to avoid selling in high-volatility conditions.',
  },
  vix: {
    title: 'India VIX',
    body: 'Volatility Index — measures market\u2019s expectation of near-term volatility. High VIX (>20) = fearful market, Low VIX (<14) = calm market. Affects option premiums directly.',
  },
  cooldown: {
    title: 'VIX Cooldown',
    body: 'A protective state triggered when VIX RSI exceeds the threshold. During cooldown, new entries are paused to avoid selling options when volatility is spiking.',
  },

  /* ─── Margin & Risk ─── */
  margin: {
    title: 'Margin',
    body: 'The amount blocked by the broker as collateral for your open positions. Higher margin = larger position size. Margin is released when positions are closed.',
  },
  marginDrawdown: {
    title: 'Margin Drawdown',
    body: 'The percentage of your maximum loss limit that has been consumed. At 100%, the system triggers a panic exit. The bar shows: current loss / 2% of margin allocated.',
  },
  maxDrawdown: {
    title: 'Max Drawdown',
    body: 'The largest peak-to-trough decline in your equity. A max drawdown of 5% means at worst, your portfolio was down 5% from its highest point.',
  },
  panicExit: {
    title: 'Panic Exit',
    body: 'Emergency square-off of all positions in a set. Triggered manually or automatically when drawdown limit is breached. All open legs are market-closed immediately.',
  },

  /* ─── Strategies ─── */
  rsiStraddle: {
    title: 'RSI Straddle Strategy',
    body: 'Sells both a Call and Put option at the same strike (ATM straddle) when RSI conditions are met. Profits from time decay when the market stays range-bound.',
  },
  trending: {
    title: 'Trending Strategy',
    body: 'A directional strategy that takes positions aligned with the market trend. Uses momentum indicators to identify trend direction and enter accordingly.',
  },
  positionType: {
    title: 'Position Type',
    body: 'The option structure being used — Straddle (same strike CE+PE), Strangle (different strikes), or custom. Determines risk/reward profile.',
  },

  /* ─── Execution ─── */
  executionSet: {
    title: 'Execution Set',
    body: 'A named group of strategy parameters that run together. Each set independently manages entries, exits, SL, and targets for its assigned underlying.',
  },
  setName: {
    title: 'Set Name',
    body: 'A unique identifier for each execution set. Helps you quickly identify which strategy instance is running (e.g., "RSI Set 1" for NIFTY, "Trend Set 1" for FINNIFTY).',
  },
  underlying: {
    title: 'Underlying',
    body: 'The index or stock on which options are traded — NIFTY, BANKNIFTY, FINNIFTY, etc. Each execution set is configured for one specific underlying.',
  },
  lots: {
    title: 'Lots',
    body: 'The number of option contracts per leg. Total Quantity = Lots × Lot Size. Lot sizes: NIFTY = 25, BANKNIFTY = 15, FINNIFTY = 25, SENSEX = 10, MIDCPNIFTY = 50, BANKEX = 15. Lot sizes are fetched from exchange data. More lots = larger position and risk.',
  },
  totalQty: {
    title: 'Total Quantity',
    body: 'The actual number of units being traded, calculated as Lots × Lot Size. For example, 2 lots of NIFTY = 2 × 25 = 50 units. This is the quantity sent to the broker in each order.',
  },
  expiry: {
    title: 'Expiry Type',
    body: 'Weekly or monthly — determines which expiry date the options are traded on. Weekly expiries have faster time decay but higher gamma risk.',
  },
  legs: {
    title: 'Legs',
    body: 'Individual option positions within a set. A straddle has 2 legs (CE + PE). Additional legs may be added as adjustments. "Open" legs are still active in the market.',
  },
  mode: {
    title: 'Execution Mode',
    body: 'Single = trades for one client only. Multi = same strategy parameters applied across multiple client accounts simultaneously.',
  },
  status: {
    title: 'Status',
    body: 'Running = actively monitoring and trading. Paused = temporarily stopped but positions remain open. Stopped = fully inactive, all positions closed.',
  },
  partialExit: {
    title: 'Partial Exit',
    body: 'Close a percentage of your CE and PE legs separately (e.g., exit 50% of calls). Useful for booking partial profits while keeping remaining exposure.',
  },

  /* ─── Entry / SL / Target ─── */
  entryPrice: {
    title: 'Entry Price',
    body: 'The price at which the option was sold (for sellers) or bought. This is your fill price and determines your cost basis.',
  },
  slPrice: {
    title: 'Stop Loss (SL)',
    body: 'The price at which the position will be automatically exited to limit losses. If option price rises above SL, a buy order is placed.',
  },
  targetPrice: {
    title: 'Target Price',
    body: 'The price at which profits are booked. For option sellers, this is the lower price where you buy back. For buyers, the higher price where you sell.',
  },

  /* ─── Stats & Dashboard ─── */
  totalSets: {
    title: 'Total Sets',
    body: 'The total number of execution sets configured in the system, including running, paused, and stopped sets.',
  },
  running: {
    title: 'Running Sets',
    body: 'Number of execution sets currently active and monitoring the market for trade signals.',
  },
  totalPnl: {
    title: 'Total P&L',
    body: 'The combined realized profit or loss across all execution sets for the current day.',
  },
  brokers: {
    title: 'Connected Brokers',
    body: 'Number of broker accounts currently connected and authenticated. Orders are placed through these broker connections.',
  },
  winRate: {
    title: 'Win Rate',
    body: 'Percentage of trading days that ended with a profit. A win rate above 60% combined with a good risk-reward ratio indicates a robust strategy.',
  },
  sharpeRatio: {
    title: 'Sharpe Ratio',
    body: 'Risk-adjusted return metric. Sharpe > 1 is good, > 2 is excellent. It measures how much excess return you earn per unit of risk taken.',
  },
  avgDailyPnl: {
    title: 'Avg Daily P&L',
    body: 'Average profit or loss per trading day. Calculated by dividing total P&L by the number of trading days in the selected period.',
  },
  maxProfit: {
    title: 'Max Profit Day',
    body: 'The single best trading day — the highest daily profit achieved in the selected period.',
  },
  maxLoss: {
    title: 'Max Loss Day',
    body: 'The single worst trading day — the largest daily loss incurred in the selected period.',
  },

  /* ─── Broker & System ─── */
  zerodha: {
    title: 'Zerodha (Kite)',
    body: 'One of the connected brokers. Orders are placed via Kite Connect API v3. Supports equities, F&O, and currency derivatives.',
  },
  angelOne: {
    title: 'Angel One (SmartAPI)',
    body: 'Connected broker using SmartAPI for order placement. Supports all exchange segments. API key rotation is handled automatically.',
  },
  marketFeed: {
    title: 'Market Feed',
    body: 'Real-time price data streamed via WebSocket from NSE. Provides live bid/ask, LTP, volume, and open interest for all subscribed instruments.',
  },
  engine: {
    title: 'Trading Engine',
    body: 'The core system that monitors strategies, evaluates conditions, and places orders. "2 active" means 2 strategy engines are currently processing.',
  },
  ordersApi: {
    title: 'Orders API',
    body: 'The gateway service that routes order requests to the correct broker. Handles order validation, rate limiting, and retry logic.',
  },
  marketHours: {
    title: 'Market Hours',
    body: 'Indian market trading session: 09:15 – 15:30 IST. "Open" means the market is currently in session. No orders can be placed outside these hours.',
  },

  /* ─── Reports & Analytics ─── */
  dayByDay: {
    title: 'Day-by-Day Breakdown',
    body: 'A table showing daily P&L, trade count, win/loss split, and drawdown for each trading day. Click a row to filter transaction logs for that date.',
  },
  transactionLog: {
    title: 'Transaction Log',
    body: 'Detailed record of every order placed — entry, exit, SL hit, target hit, partial exit. Each row shows time, instrument, action, price, and P&L impact.',
  },
  equityCurve: {
    title: 'Equity Curve',
    body: 'A line chart of your cumulative P&L over time. An upward-sloping curve indicates consistent profitability. Sharp drops indicate drawdown periods.',
  },
  heatmap: {
    title: 'P&L Heatmap',
    body: 'A visual calendar showing daily P&L as colored cells. Green = profit day, Red = loss day. Intensity shows magnitude. Helps spot patterns across weeks.',
  },

  /* ─── Admin-Specific ─── */
  userManagement: {
    title: 'User Management',
    body: 'Admin panel for creating, editing, and deactivating trader accounts. Controls role assignments (admin/trader) and broker linkages.',
  },
  brokerManagement: {
    title: 'Broker Management',
    body: 'Configure and monitor broker API connections. Add API keys, check connection status, and manage token refresh cycles.',
  },
  strategyConfig: {
    title: 'Strategy Configuration',
    body: 'Define strategy parameters — entry conditions, SL multiplier, target percentage, lot size, underlying, expiry type, and risk limits.',
  },

  /* ─── Notifications ─── */
  slHit: {
    title: 'SL Hit Alert',
    body: 'Triggered when an option position hits its stop-loss price and is automatically exited. Shows the instrument, exit price, and loss amount.',
  },
  targetHit: {
    title: 'Target Hit Alert',
    body: 'Triggered when an option position reaches its profit target and is automatically exited. Shows the instrument, exit price, and profit amount.',
  },
  entryAlert: {
    title: 'Entry Alert',
    body: 'Triggered when the system detects a valid entry signal and places a new trade. Shows the instrument, entry price, and lot quantity.',
  },
  marginAlert: {
    title: 'Margin Alert',
    body: 'Warning when a set\'s drawdown approaches the 2% limit. Take action to prevent automatic panic exit.',
  },
  netAvailable: {
    title: 'Net Available Funds',
    body: 'Live cash available in your broker account for new trades, fetched directly from Greeksoft API every 90 seconds. Formula: Gross Available − Margin Used. Click this card to force-refresh. Shows aggregate across ALL connected brokers in multi-client mode.',
  },
}
